import type { SupportAttachment } from '@travel-planner/shared/schemas';
import type { ChangeEvent } from 'react';
import { FileText, Paperclip, Video, X } from 'lucide-react';
import { useRef } from 'react';
import { Button, Text } from '@/shared/ui';
import {
  isImageName,
  isPdfName,
  isVideoName,
} from '../lib/validate-attachment';
import css from './attachments-field.module.scss';

type Props = {
  attachments: SupportAttachment[];
  isUploading: boolean;
  onFiles: (files: File[]) => void;
  onRemove: (key: string) => void;
};

// Reporter-initiated only (file picker or an explicit clipboard paste), never
// auto-captured.
export const AttachmentsField = ({
  attachments,
  isUploading,
  onFiles,
  onRemove,
}: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onPickFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    onFiles(files);
  };

  return (
    <div className={css.root}>
      <Text as="label" size="sm" weight="medium">
        Attachments
      </Text>
      <input
        accept="image/*,video/*,application/pdf"
        className={css.hiddenInput}
        multiple
        onChange={onPickFiles}
        ref={fileInputRef}
        type="file"
      />
      <Button
        disabled={isUploading}
        icon={<Paperclip style={{ height: 14, width: 14 }} />}
        onClick={() => fileInputRef.current?.click()}
        size="sm"
        variant="outline"
      >
        {isUploading ? 'Uploading…' : 'Add attachment'}
      </Button>
      <Text as="span" className={css.help} size="xs" variant="muted">
        Optional — or paste a screenshot straight from the clipboard. Images ≤
        16MB, video ≤ 256MB, PDF ≤ 16MB.
      </Text>
      {attachments.length > 0 ? (
        <ul className={css.attachments}>
          {attachments.map((a) => (
            <li className={css.attachment} key={a.key}>
              {isImageName(a.name) ? (
                <img alt={a.name} className={css.attachmentThumb} src={a.url} />
              ) : isPdfName(a.name) ? (
                <FileText className={css.attachmentIcon} />
              ) : isVideoName(a.name) ? (
                <Video className={css.attachmentIcon} />
              ) : (
                <Paperclip className={css.attachmentIcon} />
              )}
              <Text
                as="span"
                className={css.attachmentName}
                size="xs"
                title={a.name}
              >
                {a.name}
              </Text>
              <button
                aria-label={`Remove ${a.name}`}
                className={css.attachmentRemove}
                onClick={() => onRemove(a.key)}
                type="button"
              >
                <X style={{ height: 14, width: 14 }} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};
